import html2pdf from "html2pdf.js";

/**
 * Generates and downloads a BL PDF
 * @param {Object} livraison - Full livraison object with populated items
 * @param {string} mode - "a4" for full page or "receipt" for thermal
 * @param {Array} modeReglements - Available mode reglements for label lookup
 * @param {boolean} returnBlob - If true, returns a promise resolving to the PDF blob instead of downloading
 */
export const generateBLPdf = (livraison, mode = "a4", modeReglements = [], returnBlob = false) => {
  if (!livraison) return;

  const formatDate = (date) => {
    if (!date) return "-";
    const d = new Date(date);
    return d.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatPrice = (val) => {
    const n = parseFloat(val) || 0;
    return n.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  let allItems = livraison.items || [];
  if (allItems.length === 0 && livraison.stock_id) {
    allItems = [{
      stock_id: livraison.stock_id,
      quantity: livraison.quantity_declared || 1,
      unit_price: livraison.sell_price || 0,
      is_return: false,
    }];
  }

  // Calculate totals
  let grossTotal = 0;
  let totalReturns = 0;

  allItems.forEach((item) => {
    const unitPrice = parseFloat(item.unit_price) || 0;
    const quantity = parseInt(item.quantity) || 1;
    const lineTotal = unitPrice * quantity;

    if (item.is_return) {
      totalReturns += lineTotal;
    } else {
      grossTotal += lineTotal;
    }
  });

  // Apply global remise
  const globalRemise = parseFloat(livraison.remise) || 0;
  const totalRemise = grossTotal * (globalRemise / 100);
  const netAfterRemise = grossTotal - totalRemise;
  const netAmount = netAfterRemise - totalReturns;

  // Group payment by mode_reglement
  const paymentBreakdown = {};
  if (livraison.reglements && livraison.reglements.length > 0) {
    livraison.reglements.forEach((r) => {
      const modeId = r.mode_reglement?._id || r.mode_reglement || "non_specifie";
      const modeName =
        r.mode_reglement?.value ||
        modeReglements.find((m) => m._id === modeId)?.value ||
        "Non spécifié";
      paymentBreakdown[modeName] = (paymentBreakdown[modeName] || 0) + (parseFloat(r.amount) || 0);
    });
  } else if (livraison.mode_reglement) {
    const modeId = livraison.mode_reglement?._id || livraison.mode_reglement || "non_specifie";
    const modeName =
      livraison.mode_reglement?.value ||
      modeReglements.find((m) => m._id === modeId)?.value ||
      "Non spécifié";
    paymentBreakdown[modeName] = netAmount;
  }

  const reference = livraison.reference || livraison._id?.slice(-8) || "-";
  const statusLabel = livraison.status?.value || "-";

  let html = "";

  if (mode === "a4") {
    // ============ A4 PDF — exact replica of the provided image ============
    html = `
    <div style="width:210mm; min-height:297mm; padding:15mm 18mm; font-family:'Segoe UI',Arial,sans-serif; color:#000; font-size:11pt; line-height:1.4; box-sizing:border-box;">

      <!-- Header -->
      <div style="text-align:right; margin-top:30px; margin-bottom:18px; padding-bottom:5px; border-bottom:1.5px solid #333;">
        <h1 style="margin:0; font-size:16pt; letter-spacing:1px; font-weight:bold; color:#1a1a1a;">BON DE LIVRAISON</h1>
      </div>

      <!-- Info Block: Facturer left, Meta right -->
      <div style="display:flex; justify-content:space-between; margin-bottom:18px;">
        <!-- Left: Facturer -->
        <div>
          <div style="font-size:9pt; color:#555; margin-bottom:2px; font-weight:bold;">Facturer</div>
          <div style="font-size:13pt; font-weight:bold;">${livraison.buyer || "-"}</div>
          ${livraison.adresse ? `<div style="font-size:10pt; color:#444; margin-top:2px;">Ville: ${livraison.adresse}</div>` : ""}
          ${livraison.buyer_phone ? `<div style="font-size:10pt; color:#444;">Téléphone: ${livraison.buyer_phone}</div>` : ""}
        </div>
        <!-- Right: Bon info table -->
        <div>
          <table style="border-collapse:collapse; font-size:10pt;">
            <tr>
              <td style="padding:3px 10px; font-weight:bold; text-align:right;">Bon N:</td>
              <td style="padding:3px 10px;">${reference}</td>
            </tr>
            <tr>
              <td style="padding:3px 10px; font-weight:bold; text-align:right;">Date:</td>
              <td style="padding:3px 10px;">${formatDate(livraison.created)}</td>
            </tr>
            <tr>
              <td style="padding:3px 10px; font-weight:bold; text-align:right;">Statut de paiement:</td>
              <td style="padding:3px 10px;">${statusLabel}</td>
            </tr>
          </table>
        </div>
      </div>

      <!-- Items Table -->
      <table style="width:100%; border-collapse:collapse; font-size:10pt; margin-bottom:0;">
        <thead>
          <tr style="background-color:#f8f9fa;">
            <th style="border:1px solid #bbb; padding:6px 8px; text-align:center; width:30px; font-size:9pt;">#</th>
            <th style="border:1px solid #bbb; padding:6px 8px; text-align:left; font-size:9pt;">Description</th>
            <th style="border:1px solid #bbb; padding:6px 8px; text-align:center; width:65px; font-size:9pt;">Quantité</th>
            <th style="border:1px solid #bbb; padding:6px 8px; text-align:right; width:85px; font-size:9pt;">Prix unitaire</th>
            <th style="border:1px solid #bbb; padding:6px 8px; text-align:right; width:85px; font-size:9pt;">Total</th>
          </tr>
        </thead>
        <tbody>
          ${allItems.map((item, i) => {
      const unitPrice = parseFloat(item.unit_price) || 0;
      const quantity = parseInt(item.quantity) || 1;
      const lineTotal = unitPrice * quantity;
      const productName = item.stock_id?.product_name || "Produit supprimé";
      const isReturn = item.is_return;
      const bgColor = isReturn ? "#fef2f2" : (i % 2 === 1 ? "#fafafa" : "#fff");
      const textColor = isReturn ? "#dc2626" : "#000";
      return `
            <tr style="background-color:${bgColor}; color:${textColor};">
              <td style="border:1px solid #ddd; padding:5px 8px; text-align:center;">${i + 1}</td>
              <td style="border:1px solid #ddd; padding:5px 8px;">${productName}${isReturn ? " (Retour)" : ""}</td>
              <td style="border:1px solid #ddd; padding:5px 8px; text-align:center;">${quantity}</td>
              <td style="border:1px solid #ddd; padding:5px 8px; text-align:right;">${formatPrice(unitPrice)}</td>
              <td style="border:1px solid #ddd; padding:5px 8px; text-align:right;">${isReturn ? "-" : ""}${formatPrice(lineTotal)}</td>
            </tr>`;
    }).join("")}
          <!-- Total row inside the table -->
          <tr>
            <td colspan="3" style="border:none; padding:0;"></td>
            <td style="border:1px solid #999; padding:6px 8px; font-weight:bold; font-size:10pt;">Total</td>
            <td style="border:1px solid #999; padding:6px 8px; text-align:right; font-weight:bold; font-size:10pt;">${formatPrice(grossTotal)} Dh</td>
          </tr>
          ${totalRemise > 0 ? `
          <tr>
            <td colspan="3" style="border:none; padding:0;"></td>
            <td style="border:none; padding:4px 8px; font-weight:bold; color:#16a34a;">Remise (${globalRemise}%)</td>
            <td style="border:none; padding:4px 8px; text-align:right; color:#16a34a;">-${formatPrice(totalRemise)} Dh</td>
          </tr>` : ""}
          ${totalReturns > 0 ? `
          <tr>
            <td colspan="3" style="border:none; padding:0;"></td>
            <td style="border:none; padding:4px 8px; font-weight:bold; color:#dc2626;">Retours</td>
            <td style="border:none; padding:4px 8px; text-align:right; color:#dc2626;">-${formatPrice(totalReturns)} Dh</td>
          </tr>` : ""}
          ${(totalRemise > 0 || totalReturns > 0) ? `
          <tr>
            <td colspan="3" style="border:none; padding:0;"></td>
            <td style="border:none; padding:6px 8px; font-weight:bold; font-size:11pt; border-top:2px solid #333;">Net à payer</td>
            <td style="border:none; padding:6px 8px; text-align:right; font-weight:bold; font-size:11pt; border-top:2px solid #333;">${formatPrice(netAmount)} Dh</td>
          </tr>` : ""}
        </tbody>
      </table>

      <!-- Mode de paiement below the table -->
      <div style="margin-top:15px;">
        <div style="font-weight:bold; font-size:10pt; margin-bottom:8px;">Mode de paiement:</div>
        <table style="border-collapse:collapse; font-size:10pt; margin-left:10px;">
          ${Object.entries(paymentBreakdown).map(([modeName, amount]) => `
          <tr>
            <td style="padding:3px 30px 3px 0;">${modeName}:</td>
            <td style="padding:3px 0; text-align:right; min-width:100px;">${formatPrice(amount)} Dh</td>
          </tr>`).join("")}
          <tr>
            <td style="padding:6px 30px 3px 0; font-weight:bold;">Montant payé:</td>
            <td style="padding:6px 0 3px 0; text-align:right; font-weight:bold; min-width:100px;">${formatPrice(netAmount)} Dh</td>
          </tr>
        </table>
      </div>

      <div style="text-align:right; font-size:8pt; color:#999; margin-top:40px;">Page 1</div>
    </div>`;
  } else {
    // ============ RECEIPT PDF — 80mm thermal ============
    html = `
    <div style="width:72mm; padding:3mm; font-family:'Courier New',monospace; color:#000; font-size:9pt; line-height:1.3;">

      <div style="text-align:right; margin-bottom:5px;">
        <div style="font-size:10pt; font-weight:bold;">BON DE LIVRAISON</div>
      </div>

      <div style="border-top:1px dashed #000; margin:4px 0;"></div>

      <div style="font-size:8pt; margin-bottom:3px;">
        <div><strong>N°:</strong> ${reference}</div>
        <div><strong>Date:</strong> ${formatDate(livraison.created)}</div>
        <div><strong>Client:</strong> ${livraison.buyer || "-"}</div>
        ${livraison.buyer_phone ? `<div><strong>Tél:</strong> ${livraison.buyer_phone}</div>` : ""}
        ${livraison.adresse ? `<div><strong>Adr:</strong> ${livraison.adresse}</div>` : ""}
      </div>

      <div style="border-top:1px dashed #000; margin:4px 0;"></div>

      <table style="width:100%; border-collapse:collapse; font-size:8pt;">
        <thead>
          <tr>
            <th style="text-align:left; border-bottom:1px solid #000; padding:2px 0; font-size:7pt;">Article</th>
            <th style="text-align:right; border-bottom:1px solid #000; padding:2px 0; font-size:7pt; width:25px;">Qté</th>
            <th style="text-align:right; border-bottom:1px solid #000; padding:2px 0; font-size:7pt; width:40px;">P.U</th>
            <th style="text-align:right; border-bottom:1px solid #000; padding:2px 0; font-size:7pt; width:45px;">Total</th>
          </tr>
        </thead>
        <tbody>
          ${allItems.map((item) => {
      const unitPrice = parseFloat(item.unit_price) || 0;
      const quantity = parseInt(item.quantity) || 1;
      const lineTotal = unitPrice * quantity;
      const productName = item.stock_id?.product_name || "Produit";
      const color = item.is_return ? "#666" : "#000";
      return `
            <tr style="color:${color};">
              <td style="padding:2px 0; vertical-align:top;">${productName}${item.is_return ? " (R)" : ""}</td>
              <td style="padding:2px 0; text-align:right;">${quantity}</td>
              <td style="padding:2px 0; text-align:right;">${formatPrice(unitPrice)}</td>
              <td style="padding:2px 0; text-align:right;">${item.is_return ? "-" : ""}${formatPrice(lineTotal)}</td>
            </tr>`;
    }).join("")}
        </tbody>
      </table>

      <div style="border-top:1px dashed #000; margin:4px 0;"></div>

      <div style="font-size:9pt;">
        <div style="display:flex; justify-content:space-between; padding:1px 0;">
          <span>Total:</span><span>${formatPrice(grossTotal)} Dh</span>
        </div>
        ${totalRemise > 0 ? `
        <div style="display:flex; justify-content:space-between; padding:1px 0; color:#333;">
          <span>Remise (${globalRemise}%):</span><span>-${formatPrice(totalRemise)} Dh</span>
        </div>` : ""}
        ${totalReturns > 0 ? `
        <div style="display:flex; justify-content:space-between; padding:1px 0;">
          <span>Retours:</span><span>-${formatPrice(totalReturns)} Dh</span>
        </div>` : ""}
        <div style="border-top:1px dashed #000; margin:3px 0;"></div>
        <div style="display:flex; justify-content:space-between; padding:2px 0; font-weight:bold; font-size:11pt;">
          <span>NET:</span><span>${formatPrice(netAmount)} Dh</span>
        </div>
      </div>

      <div style="border-top:1px dashed #000; margin:4px 0;"></div>

      <div style="font-size:8pt;">
        ${Object.entries(paymentBreakdown).map(([modeName, amount]) => `
        <div style="display:flex; justify-content:space-between; padding:1px 0;">
          <span>${modeName}:</span><span>${formatPrice(amount)} Dh</span>
        </div>`).join("")}
      </div>

      <div style="border-top:1px dashed #000; margin:4px 0;"></div>
    </div>`;
  }

  // Create temporary container
  const container = document.createElement("div");
  container.style.position = "absolute";
  container.style.left = "-9999px";
  container.style.top = "0";
  container.innerHTML = `<div><style>* { border-radius: 0 !important; -webkit-border-radius: 0 !important; }</style>${html}</div>`;
  document.body.appendChild(container);

  const element = container.firstElementChild;

  const filename = `BL-${reference}-${mode === "receipt" ? "ticket" : "A4"}.pdf`;

  const options =
    mode === "a4"
      ? {
        margin: 0,
        filename,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      }
      : {
        margin: 0,
        filename,
        image: { type: "jpeg", quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true, width: 302 },
        jsPDF: { unit: "mm", format: [80, 200], orientation: "portrait" },
      };

  if (returnBlob) {
    return html2pdf()
      .set(options)
      .from(element)
      .output('blob')
      .then(blob => {
        document.body.removeChild(container);
        return blob;
      })
      .catch(err => {
        document.body.removeChild(container);
        throw err;
      });
  }

  html2pdf()
    .set(options)
    .from(element)
    .save()
    .then(() => {
      document.body.removeChild(container);
    })
    .catch((err) => {
      console.error("PDF generation error:", err);
      document.body.removeChild(container);
    });
};

export default generateBLPdf;
